import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, Shield, CheckCircle, Loader, Brain } from 'lucide-react';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Terminal from './Terminal';
import SEOHead from './SEOHead';
import { generateHomeSEO } from '../utils/seoUtils';
import xtraceLogo from '../assets/xtrace-logo.svg';
import './Home.css';

const Home = () => {
  const [newsContent, setNewsContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [error, setError] = useState('');
  const [showTerminal, setShowTerminal] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationProgress, setVerificationProgress] = useState(0);
  const [verificationStep, setVerificationStep] = useState('');
  const [pendingReportId, setPendingReportId] = useState('');
  // Captcha is disabled by default; the server's /api/config is authoritative.
  const [siteConfig, setSiteConfig] = useState({ hcaptcha_enabled: false, hcaptcha_sitekey: '' });
  const hcaptchaRef = useRef(null);
  const navigate = useNavigate();

  // The server decides whether a captcha challenge is required, so the same
  // build works in the dev container and in production.
  useEffect(() => {
    let cancelled = false;
    axios
      .get('/api/config')
      .then((res) => {
        if (!cancelled && res.data) setSiteConfig(res.data);
      })
      .catch(() => {
        /* keep the default: no captcha challenge */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newsContent.trim()) {
      setError('Please enter news content to verify');
      return;
    }

    if (!siteConfig.hcaptcha_enabled) {
      handleVerifyNews('');
      return;
    }
    setShowCaptcha(true);
  };

  const onCaptchaVerify = (token) => {
    handleVerifyNews(token);
  };

  const handleVerifyNews = async (token) => {
    setLoading(true);
    setError('');

    try {
      const newSessionId = Date.now().toString() + Math.random().toString(36).substr(2, 9);
      setSessionId(newSessionId);

      const response = await axios.post('/api/verify-news', {
        news_content: newsContent,
        hcaptcha_token: token,
        session_id: newSessionId
      });

      if (response.data.success) {
        if (response.data.report_id) {
          setPendingReportId(response.data.report_id);
          setShowCaptcha(false);
          setShowTerminal(true);
          setIsVerifying(true);
        } else {
          setError(response.data.message || 'Server is busy. Please try again later.');
          setShowCaptcha(false);
          hcaptchaRef.current?.resetCaptcha();
        }
        setLoading(false);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to verify news. Please try again.');
      setShowCaptcha(false);
      hcaptchaRef.current?.resetCaptcha();
      setLoading(false);
    }
  };

  const handleTerminalComplete = (data) => {
    setShowTerminal(false);
    setIsVerifying(false);
    setVerificationProgress(0);
    setVerificationStep('');
    setPendingReportId('');
    navigate(`/report/${data.report_id}`);
  };

  const handleTerminalClose = () => {
    setShowTerminal(false);
    // Don't reset verification state if still verifying
    if (!isVerifying) {
      setNewsContent('');
      setShowCaptcha(false);
      hcaptchaRef.current?.resetCaptcha();
    }
  };

  const handleTerminalError = () => {
    setIsVerifying(false);
    setVerificationProgress(0);
    setVerificationStep('');
    setShowTerminal(false);
    setNewsContent('');
    setShowCaptcha(false);
    setPendingReportId('');
    hcaptchaRef.current?.resetCaptcha();
  };

  const reopenTerminal = () => {
    setShowTerminal(true);
  };

  const handleProgressUpdate = (progress, step) => {
    setVerificationProgress(progress);
    setVerificationStep(step);
  };

  // Fallback polling in case the socket completion event is missed.
  // Polls slowly and only after a grace period, so the console is not flooded
  // with expected 404s while the verification is still running.
  useEffect(() => {
    if (!isVerifying || !pendingReportId) return;
    let cancelled = false;
    let interval;
    const start = setTimeout(() => {
      interval = setInterval(async () => {
        try {
          await axios.get(`/api/reports/${pendingReportId}`);
          if (!cancelled) {
            setShowTerminal(false);
            setIsVerifying(false);
            navigate(`/report/${pendingReportId}`);
            setPendingReportId('');
          }
        } catch (e) {
          // Report is not ready yet; keep polling until the timeout below.
        }
      }, 5000);
    }, 30000);
    const timeout = setTimeout(() => {
      if (interval) clearInterval(interval);
    }, 600000);
    return () => {
      cancelled = true;
      clearTimeout(start);
      if (interval) clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [isVerifying, pendingReportId, navigate]);

  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Generate SEO data for home page
  const seoData = generateHomeSEO();

  return (
    <div className="home">
      {/* SEO Meta Tags */}
      <SEOHead {...seoData} />
      
      {/* Geometric Background Shapes */}
      <div className="geometric-bg">
        <div className="geometric-shape shape-1"></div>
        <div className="geometric-shape shape-2"></div>
        <div className="geometric-shape shape-3"></div>
      </div>
      
      <div className="container">
        <motion.div 
          className="home-content"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="hero-section">
            <motion.h1 
              className="hero-title"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Verify The <span className="gradient-text">News</span><br />
              With <span className="gradient-text">AI Intelligence</span>
            </motion.h1>
            
            <motion.p 
              className="hero-subtitle"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
            VeryNews breaks a claim into checkable parts, verifies each against trusted sources, and remembers what it proves.
            </motion.p>

            <motion.div 
              className="tagline"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <Shield className="tagline-icon pulse" />
              <span>Truth in Every News</span>
            </motion.div>
          </div>

          <motion.div 
            className="search-section glass-effect"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            <form onSubmit={handleSubmit} className="search-form">
              <div className="search-input-wrapper">
                <textarea
                  className="search-input"
                  placeholder="Paste your news content here to verify its authenticity..."
                  value={newsContent}
                  onChange={(e) => setNewsContent(e.target.value)}
                  rows="4"
                  disabled={loading}
                />
                <Search className="search-icon" />
              </div>
              
              {error && (
                <motion.div 
                  className="error-message"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {error}
                </motion.div>
              )}

              {!showCaptcha && (
                <button 
                  type="submit" 
                  className="button-primary search-button"
                  disabled={loading || !newsContent.trim()}
                >
                  {loading ? (
                    <>
                      <Loader className="spinner" />
                      Verifying...
                    </>
                  ) : (
                    'Verify The News'
                  )}
                </button>
              )}

              {showCaptcha && siteConfig.hcaptcha_enabled && (
                <motion.div 
                  className="captcha-wrapper"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <HCaptcha
                    ref={hcaptchaRef}
                    sitekey={siteConfig.hcaptcha_sitekey || '9148d35a-42a2-4bd2-b154-fb06606a909e'}
                    onVerify={onCaptchaVerify}
                  />
                </motion.div>
              )}

            </form>
            
            {/* Verification in Progress Indicator */}
            {isVerifying && !showTerminal && (
              <motion.div 
                className="verification-indicator glass-effect"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="indicator-content">
                  <div className="indicator-header">
                    <Loader className="spinner" />
                    <span>Verification in Progress</span>
                  </div>
                  <div className="mini-progress">
                    <div className="mini-progress-bar">
                      <motion.div
                        className="mini-progress-fill"
                        initial={{ width: 0 }}
                        animate={{ width: `${verificationProgress}%` }}
                        transition={{ duration: 0.5 }}
                      />
                    </div>
                    <span className="mini-progress-text">{verificationProgress}%</span>
                  </div>
                  <p className="indicator-step">{verificationStep}</p>
                  <button 
                    className="reopen-terminal-btn"
                    onClick={reopenTerminal}
                  >
                    View Terminal
                  </button>
                </div>
              </motion.div>
            )}
          </motion.div>

          <motion.div 
            className="features-section"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.0 }}
          >
            <div className="features-grid">
              <div className="feature-card glass-effect">
                <div className="feature-icon">
                  <Shield />
                </div>
                <h3>Claim-Level Analysis</h3>
                <p>Every claim checked on its own, not the story as a whole</p>
              </div>
              
              <div className="feature-card glass-effect">
                <div className="feature-icon">
                  <CheckCircle />
                </div>
                <h3>Evidence You Can See</h3>
                <p>Each verdict shows the quote and the source behind it</p>
              </div>
              
              <div className="feature-card glass-effect">
                <div className="feature-icon">
                  <Search />
                </div>
                <h3>Trusted Sources</h3>
                <p>Cross-referenced against established news outlets</p>
              </div>

              <div className="feature-card glass-effect feature-card-memory">
                <div className="feature-icon">
                  <Brain />
                </div>
                <h3>Memory Is All You Need</h3>
                <p>Findings persist, so every check starts smarter than the last</p>
              </div>
            </div>
          </motion.div>

          <motion.div 
            className="supporters-section"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
          >
            <h2 className="supporters-title">Supported By</h2>
            <div className="supporters-grid">
              <div className="supporter-item">
                <img
                  src={xtraceLogo}
                  alt="XTrace Memory Logo"
                  className="supporter-logo"
                />
                <span className="supporter-name">XTrace Memory</span>
              </div>
              <div className="supporter-item">
                <img 
                  src="https://www.tavily.com/logos/tavily-full.svg" 
                  alt="Tavily Logo" 
                  className="supporter-logo"
                />
                <span className="supporter-name">Tavily</span>
              </div>
              <div className="supporter-item">
                <img 
                  src="https://www.gstatic.com/cgc/google-cloud-logo.svg" 
                  alt="Google Cloud Platform Logo" 
                  className="supporter-logo"
                />
                <span className="supporter-name">GCP</span>
              </div>
              <div className="supporter-item">
                <img 
                  src="https://phicoin.net/img/logo.png" 
                  alt="Phi Lab Logo" 
                  className="supporter-logo"
                />
                <span className="supporter-name">Phi Lab</span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
      
      {/* Terminal Modal */}
      {showTerminal && (
        <Terminal
          sessionId={sessionId}
          onComplete={handleTerminalComplete}
          onClose={handleTerminalClose}
          onError={handleTerminalError}
          onProgress={handleProgressUpdate}
        />
      )}
    </div>
  );
};

export default Home; 